import React, { useState } from "react";
import { useCreateGreenMilesHotel } from "./hooks/useCreateGreenMilesHotel";

const GreenMilesHotelForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    postalCode: "",
    city: "",
    country: "France",
    stars: 3,
    numberOfRooms: "",
  });

  const [validationErrors, setValidationErrors] = useState({});
  const { createHotel, isLoading, error } = useCreateGreenMilesHotel();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Effacer l'erreur de validation pour ce champ
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Le nom de l'hôtel est obligatoire";
    }

    if (!formData.address.trim()) {
      errors.address = "L'adresse est obligatoire";
    }

    if (!formData.city.trim()) {
      errors.city = "La ville est obligatoire";
    }

    if (!formData.numberOfRooms) {
      errors.numberOfRooms = "Le nombre de chambres est obligatoire";
    } else {
      const rooms = parseInt(formData.numberOfRooms);
      if (isNaN(rooms) || rooms < 1 || rooms > 1000) {
        errors.numberOfRooms = "Le nombre de chambres doit être entre 1 et 1000";
      }
    }

    if (formData.stars && (formData.stars < 1 || formData.stars > 5)) {
      errors.stars = "Le nombre d'étoiles doit être entre 1 et 5";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const result = await createHotel({
      ...formData,
      numberOfRooms: parseInt(formData.numberOfRooms),
      stars: parseInt(formData.stars),
    });

    if (result.success) {
      onSuccess?.(result.data, result.message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white shadow-sm rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Informations de l'hôtel</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Nom de l'hôtel */}
          <div className="md:col-span-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Nom de l'hôtel *
            </label>
            <input type="text" id="name" name="name" value={formData.name} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#365644] ${validationErrors.name ? "border-red-500" : "border-gray-300"}`} placeholder="Ex: Hôtel Le Grand Paris" />
            {validationErrors.name && <p className="mt-1 text-sm text-red-600">{validationErrors.name}</p>}
          </div>

          {/* Adresse */}
          <div className="md:col-span-2">
            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-1">
              Adresse *
            </label>
            <input type="text" id="address" name="address" value={formData.address} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#365644] ${validationErrors.address ? "border-red-500" : "border-gray-300"}`} placeholder="Ex: 123 Rue de la Paix" />
            {validationErrors.address && <p className="mt-1 text-sm text-red-600">{validationErrors.address}</p>}
          </div>

          {/* Code postal */}
          <div>
            <label htmlFor="postalCode" className="block text-sm font-medium text-gray-700 mb-1">
              Code postal
            </label>
            <input type="text" id="postalCode" name="postalCode" value={formData.postalCode} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#365644]" placeholder="Ex: 75001" />
          </div>

          {/* Ville */}
          <div>
            <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-1">
              Ville *
            </label>
            <input type="text" id="city" name="city" value={formData.city} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#365644] ${validationErrors.city ? "border-red-500" : "border-gray-300"}`} placeholder="Ex: Paris" />
            {validationErrors.city && <p className="mt-1 text-sm text-red-600">{validationErrors.city}</p>}
          </div>

          {/* Pays */}
          <div>
            <label htmlFor="country" className="block text-sm font-medium text-gray-700 mb-1">
              Pays
            </label>
            <input type="text" id="country" name="country" value={formData.country} onChange={handleInputChange} className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#365644]" placeholder="Ex: France" />
          </div>

          {/* Étoiles */}
          <div>
            <label htmlFor="stars" className="block text-sm font-medium text-gray-700 mb-1">
              Nombre d'étoiles
            </label>
            <select id="stars" name="stars" value={formData.stars} onChange={handleInputChange} className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#365644] ${validationErrors.stars ? "border-red-500" : "border-gray-300"}`}>
              {[1, 2, 3, 4, 5].map((star) => (
                <option key={star} value={star}>
                  {star} étoile{star > 1 ? "s" : ""}
                </option>
              ))}
            </select>
            {validationErrors.stars && <p className="mt-1 text-sm text-red-600">{validationErrors.stars}</p>}
          </div>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Configuration des chambres</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Nombre de chambres */}
          <div>
            <label htmlFor="numberOfRooms" className="block text-sm font-medium text-gray-700 mb-1">
              Nombre de chambres *
            </label>
            <input type="number" id="numberOfRooms" name="numberOfRooms" value={formData.numberOfRooms} onChange={handleInputChange} min="1" max="1000" className={`w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-[#365644] ${validationErrors.numberOfRooms ? "border-red-500" : "border-gray-300"}`} placeholder="Ex: 50" />
            {validationErrors.numberOfRooms && <p className="mt-1 text-sm text-red-600">{validationErrors.numberOfRooms}</p>}
          </div>

          <div className="flex items-end">
            <div className="bg-green-50 border border-green-200 rounded-md p-3 text-sm text-green-800">
              <div className="flex items-center">
                <span className="mr-2">ℹ️</span>
                <span>Les chambres seront créées automatiquement avec numérotation séquentielle</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Erreur globale */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <span className="text-red-400 mr-2">❌</span>
            <div className="text-sm text-red-800">
              <strong>Erreur:</strong> {error}
            </div>
          </div>
        </div>
      )}

      {/* Boutons d'action */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#365644]" disabled={isLoading}>
          Annuler
        </button>
        <button type="submit" disabled={isLoading} className="px-4 py-2 text-sm font-medium text-white bg-[#365644] border border-transparent rounded-md shadow-sm hover:bg-[#2d4c3c] focus:outline-none focus:ring-2 focus:ring-[#365644] disabled:opacity-50 disabled:cursor-not-allowed">
          {isLoading ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Création en cours...
            </div>
          ) : (
            "Créer l'hôtel GreenMiles"
          )}
        </button>
      </div>
    </form>
  );
};

export default GreenMilesHotelForm;
